import { cn } from 'noddi-ui-common';
import { ReactNode } from 'react';
import { StringParam, useQueryParam, withDefault } from 'use-query-params';
import { webButtonStyling } from '../theme';

export interface NoddiTabItem<TabValue extends string> {
  label: string;
  value: TabValue;
  node?: ReactNode;
}

export interface NoddiTabsProps<TabValue extends string> {
  items: NoddiTabItem<TabValue>[];
  value?: TabValue; // now optional for controlled
  onChange?: (value: TabValue) => void; // now optional for uncontrolled
  className?: string;
  defaultValue?: TabValue; // for uncontrolled
  queryParamKey?: string;
}

export function NoddiTabsV2<TabValue extends string>({
  items,
  value: controlledValue,
  onChange,
  defaultValue,
  className,
  queryParamKey = 'tab'
}: NoddiTabsProps<TabValue>) {
  const isControlled = controlledValue !== undefined;

  const defaultUnControlled =
    items.find((item) => item.value === defaultValue)?.value ?? (items[0]?.value || undefined);

  const [urlTab, setUrlTab] = useQueryParam(queryParamKey, withDefault(StringParam, defaultUnControlled));
  const value = isControlled ? controlledValue : urlTab;
  const activeItem = items.find((item) => item.value === value);

  const handleChange = (newValue: TabValue) => {
    if (!isControlled) {
      setUrlTab(newValue);
    }
    onChange?.(newValue);
  };

  return (
    <div className='flex flex-col gap-4'>
      <div className='overflow-x-auto'>
        <div className={cn('inline-flex w-fit rounded-full bg-grey/20 p-1', className)} role='tablist'>
          {items.map((item) => {
            const isActive = value === item.value;
            return (
              <button
                key={item.value}
                role='tab'
                aria-selected={isActive}
                tabIndex={isActive ? 0 : -1}
                className={cn(
                  'whitespace-nowrap !rounded-full px-3 py-1 font-medium transition-colors focus:outline-none',
                  isActive ? 'bg-white text-black shadow' : 'bg-transparent text-black/70',
                  webButtonStyling
                )}
                onClick={() => handleChange(item.value)}
                type='button'
              >
                {item.label}
              </button>
            );
          })}
        </div>
      </div>
      {activeItem?.node && <div>{activeItem.node}</div>}
    </div>
  );
}
